import React from 'react';
import { motion, type Variants } from 'framer-motion';
import {
    ShieldCheck,
    Lock,
    Database,
    Activity,
    Cloud,
    Trash2,
    Baby,
    RefreshCw,
    Mail,
    HeartPulse,
    FileText,
    CheckCircle2,
} from 'lucide-react';

const fadeUp: Variants = {
    hidden: { opacity: 0, y: 24 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.45,
            ease: 'easeOut',
        },
    },
};

const SECTIONS = [
    {
        id: 'information-we-collect',
        title: '1. Information We Collect',
        icon: Database,
        items: [
            {
                label: 'Account Information',
                text: 'When you register, we may collect your name, email address, and authentication credentials, including sign-in methods managed through Firebase Authentication.',
            },
            {
                label: 'Health & Biometric Data',
                text: 'To provide coaching, analysis, and personalized insights, you may provide data such as current weight, goal weight, food logs, hydration logs, and exercise activity.',
            },
            {
                label: 'Camera & Photo Library Data',
                text: 'When you use the AI Meal Inspector, we may access your camera to scan the barcode of foods.',
            },
            {
                label: 'Device & Usage Information',
                text: 'We may automatically collect information such as device type, operating system, app diagnostics, crash logs, and usage patterns to maintain and improve the Service.',
            },
        ],
    },
    {
        id: 'health-connect',
        title: '2. Google Health Connect Integration',
        icon: Activity,
        items: [
            {
                label: 'Explicit Permission Required',
                text: 'LesslyKilo integrates with Google Health Connect only when you provide explicit consent to read or write supported health and fitness data.',
            },
            {
                label: 'Strict Purpose',
                text: 'Use of Health Connect data follows the Health Connect Permissions policy, including applicable Limited Use requirements.',
            },
            {
                label: 'No Advertising Use',
                text: 'We do not use or transfer Health Connect data for advertising, marketing, or data brokering purposes.',
            },
            {
                label: 'Core Feature Use Only',
                text: 'Health Connect data may be processed only as needed to power or improve the app’s core health and coaching features.',
            },
        ],
    },
    {
        id: 'how-we-use-data',
        title: '3. How We Use Your Data',
        icon: HeartPulse,
        items: [
            {
                label: 'Core Functionality',
                text: 'We use your data to generate insights, calculate trends, provide behavior support, and deliver app features such as coaching, reports, and progress summaries.',
            },
            {
                label: 'AI Processing',
                text: 'Meal, hydration, workout, and chat inputs may be processed through our internal logic and trusted AI systems to generate personalized feedback.',
            },
            {
                label: 'Service Improvement',
                text: 'We use usage and diagnostic information to fix bugs, improve reliability, and optimize the user experience.',
            },
            {
                label: 'Communication',
                text: 'If enabled, we may send notifications about daily plans, subscription status, app reminders, and support updates.',
            },
        ],
    },
    {
        id: 'third-parties',
        title: '4. Third-Party Services and Data Sharing',
        icon: Cloud,
        items: [
            {
                label: 'No Sale of Personal Data',
                text: 'We do not sell your personal data.',
            },
            {
                label: 'AI Processors',
                text: 'Text and image data submitted through features such as the AI Coach or Meal Inspector may be securely processed by trusted AI providers, such as OpenAI or Google Cloud, only to the extent required to deliver the requested analysis.',
            },
            {
                label: 'Cloud Infrastructure',
                text: 'We use Google Firebase for user authentication, storage, and application data infrastructure.',
            },
            {
                label: 'Payment Processors',
                text: 'If you upgrade to LesslyKilo Pro, billing is handled by third-party processors such as Google Play, Apple, or Stripe. We do not store full card details on our own servers.',
            },
        ],
    },
    {
        id: 'retention-deletion',
        title: '5. Data Retention and Deletion',
        icon: Trash2,
        items: [
            {
                label: 'Retention',
                text: 'We retain personal and health-related data only for as long as your account is active or as reasonably necessary to provide the Service.',
            },
            {
                label: 'Your Right to Delete',
                text: 'You may request deletion of your account and associated data through the app settings or by contacting us. We will delete data from active systems unless we are required to retain it for legal or regulatory reasons.',
            },
        ],
    },
    {
        id: 'security',
        title: '6. Security of Your Data',
        icon: Lock,
        items: [
            {
                label: 'Protection Measures',
                text: 'We use industry-standard security measures, including encryption in transit and safeguards for stored data, to help protect your information.',
            },
            {
                label: 'Important Limitation',
                text: 'No electronic transmission or storage system can be guaranteed to be 100% secure, and absolute security cannot be promised.',
            },
        ],
    },
    {
        id: 'children',
        title: "7. Children's Privacy",
        icon: Baby,
        items: [
            {
                label: 'Age Restrictions',
                text: 'The Service is not intended for children under 13, or under 16 where required by local law.',
            },
            {
                label: 'No Knowing Collection',
                text: 'We do not knowingly collect personal information from children without appropriate legal authorization. If we learn that such data has been collected improperly, we will take steps to delete it.',
            },
        ],
    },
    {
        id: 'changes',
        title: '8. Changes to This Privacy Policy',
        icon: RefreshCw,
        items: [
            {
                label: 'Policy Updates',
                text: 'We may update this Privacy Policy to reflect operational, legal, or regulatory changes. Updates will be posted on this page and the Effective Date will be revised accordingly.',
            },
        ],
    },
    {
        id: 'contact',
        title: '9. Contact Us',
        icon: Mail,
        items: [
            {
                label: 'Email',
                text: 'caselabs91@gmail.com',
            },
            {
                label: 'Company',
                text: 'Viomath Tech Proprietary Limited',
            },
        ],
    },
] as const;

const QUICK_FACTS = [
    {
        icon: ShieldCheck,
        title: 'We do not sell your data',
        text: 'Your data is used to provide and improve LesslyKilo, not for advertising or resale.',
    },
    {
        icon: Activity,
        title: 'Health data needs your consent',
        text: 'Health Connect access only happens when you explicitly grant permission.',
    },
    {
        icon: Trash2,
        title: 'You can request deletion',
        text: 'You may request full account and data deletion through the app or by contacting us.',
    },
];

export default function PrivacyPage() {
    return (
        <section className="relative overflow-hidden py-24 md:py-32">
            {/* Background */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(34,211,238,0.10),transparent_32%)]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(16,185,129,0.08),transparent_28%)]" />
                <div className="absolute left-[-8%] top-20 h-72 w-72 rounded-full bg-cyan-400/10 blur-3xl" />
                <div className="absolute right-[-10%] bottom-10 h-80 w-80 rounded-full bg-emerald-400/10 blur-3xl" />
                <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,white_1px,transparent_1px),linear-gradient(to_bottom,white_1px,transparent_1px)] [background-size:48px_48px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-6">
                {/* Hero */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    animate="show"
                    className="mx-auto mb-16 max-w-4xl text-center"
                >
                    <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-[var(--color-brand-primary)]/20 bg-[linear-gradient(135deg,rgba(34,211,238,0.10),rgba(16,185,129,0.08))] px-4 py-2 text-xs font-extrabold uppercase tracking-[0.22em] text-[var(--color-brand-primary)] shadow-[0_8px_30px_rgba(34,211,238,0.08)]">
                        <FileText size={14} />
                        Privacy Policy
                    </div>

                    <h1 className="text-4xl font-black leading-[1.05] text-[var(--color-brand-text)] md:text-6xl">
                        Your data deserves
                        <span className="block bg-[linear-gradient(90deg,var(--color-brand-primary),#34d399)] bg-clip-text text-transparent">
              clarity, respect, and protection
            </span>
                    </h1>

                    <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-[var(--color-brand-text-secondary)] md:text-xl">
                        This page explains what LesslyKilo collects, how it is used, and the choices you have
                        when using our app and related services.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]">
                            <CheckCircle2 size={16} className="text-emerald-300" />
                            Effective Date: March 2, 2026
                        </div>
                        <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(255,255,255,0.02))] px-4 py-2 text-sm font-semibold text-[var(--color-brand-text)]">
                            <CheckCircle2 size={16} className="text-cyan-300" />
                            Provider: Viomath Tech Proprietary Limited
                        </div>
                    </div>
                </motion.div>

                {/* Summary intro */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-80px' }}
                    className="mb-10 rounded-[2.5rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(180deg,rgba(8,18,28,0.98),rgba(5,14,22,0.98))] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.18)] md:p-10"
                >
                    <div className="grid gap-6 lg:grid-cols-[1.35fr_0.95fr] lg:items-center">
                        <div>
                            <h2 className="text-3xl font-black text-[var(--color-brand-text)] md:text-4xl">
                                Privacy built for a health-focused product
                            </h2>
                            <p className="mt-5 text-[15px] leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                                LesslyKilo processes health and lifestyle information to provide personalized
                                coaching, daily insights, and progress analysis. We handle that responsibility
                                seriously and aim to explain our data practices in a clear, transparent way.
                            </p>
                            <p className="mt-4 text-[15px] leading-8 text-[var(--color-brand-text-secondary)] md:text-base">
                                By using the Service, you agree to the collection and use of information in
                                accordance with this policy.
                            </p>
                        </div>

                        <div className="grid gap-4">
                            {QUICK_FACTS.map((fact) => {
                                const Icon = fact.icon;
                                return (
                                    <div
                                        key={fact.title}
                                        className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)] p-5"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[linear-gradient(135deg,rgba(34,211,238,0.12),rgba(16,185,129,0.10))] text-[var(--color-brand-primary)]">
                                                <Icon size={22} />
                                            </div>
                                            <div>
                                                <h3 className="text-sm font-black uppercase tracking-[0.14em] text-[var(--color-brand-text)]">
                                                    {fact.title}
                                                </h3>
                                                <p className="mt-2 text-sm leading-7 text-[var(--color-brand-text-secondary)]">
                                                    {fact.text}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </motion.div>

                {/* Main content */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    {/* Sidebar */}
                    <motion.aside
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: '-80px' }}
                        className="lg:col-span-4 xl:col-span-3"
                    >
                        <div className="sticky top-24 rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,rgba(10,18,28,0.98),rgba(6,14,22,0.98))] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.12)]">
                            <h3 className="mb-4 text-sm font-black uppercase tracking-[0.18em] text-[var(--color-brand-text-secondary)]">
                                On this page
                            </h3>

                            <nav className="space-y-2">
                                {SECTIONS.map((section) => {
                                    const Icon = section.icon;
                                    return (
                                        <a
                                            key={section.id}
                                            href={`#${section.id}`}
                                            className="flex items-center gap-3 rounded-xl border border-transparent px-3 py-3 text-sm font-medium text-[var(--color-brand-text-secondary)] transition-all hover:border-[var(--color-brand-primary)]/20 hover:bg-[var(--color-brand-primary)]/8 hover:text-[var(--color-brand-text)]"
                                        >
                                            <Icon size={16} className="text-[var(--color-brand-primary)]" />
                                            <span>{section.title}</span>
                                        </a>
                                    );
                                })}
                            </nav>
                        </div>
                    </motion.aside>

                    {/* Sections */}
                    <div className="space-y-6 lg:col-span-8 xl:col-span-9">
                        {SECTIONS.map((section, idx) => {
                            const Icon = section.icon;
                            return (
                                <motion.section
                                    key={section.id}
                                    id={section.id}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="show"
                                    viewport={{ once: true, margin: '-60px' }}
                                    transition={{ delay: idx * 0.04 }}
                                    className="rounded-[2rem] border border-[var(--color-brand-border)] bg-[linear-gradient(180deg,rgba(10,18,28,0.98),rgba(6,14,22,0.98))] p-8 shadow-[0_18px_50px_rgba(0,0,0,0.12)]"
                                >
                                    <div className="mb-6 flex items-center gap-4">
                                        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,rgba(34,211,238,0.12),rgba(16,185,129,0.10))] text-[var(--color-brand-primary)] shadow-[0_10px_24px_rgba(34,211,238,0.10)]">
                                            <Icon size={26} />
                                        </div>

                                        <div>
                                            <h2 className="text-2xl font-black text-[var(--color-brand-text)] md:text-3xl">
                                                {section.title}
                                            </h2>
                                        </div>
                                    </div>

                                    <div className="space-y-5">
                                        {section.items.map((item) => (
                                            <div
                                                key={item.label}
                                                className="rounded-2xl border border-[var(--color-brand-border)] bg-[var(--color-brand-ai-bg)]/70 p-5"
                                            >
                                                <h3 className="text-sm font-black uppercase tracking-[0.16em] text-[var(--color-brand-primary)]">
                                                    {item.label}
                                                </h3>
                                                <p className="mt-3 text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-[15px]">
                                                    {item.text}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </motion.section>
                            );
                        })}
                    </div>
                </div>

                {/* Footer contact strip */}
                <motion.div
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="mt-12 rounded-[2rem] border border-[var(--color-brand-primary)]/12 bg-[linear-gradient(135deg,rgba(34,211,238,0.08),rgba(16,185,129,0.06),rgba(255,255,255,0.02))] p-6 shadow-[0_18px_50px_rgba(0,0,0,0.16)] md:p-8"
                >
                    <div className="flex flex-col items-start justify-between gap-5 md:flex-row md:items-center">
                        <div>
                            <h3 className="text-xl font-black text-[var(--color-brand-text)] md:text-2xl">
                                Questions about privacy?
                            </h3>
                            <p className="mt-2 max-w-3xl text-sm leading-7 text-[var(--color-brand-text-secondary)] md:text-base">
                                If you have any questions, concerns, or requests regarding this Privacy Policy or
                                how your information is handled, please contact us.
                            </p>
                        </div>

                        <a
                            href="mailto:caselabs91@gmail.com"
                            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-5 py-3 text-sm font-semibold text-[var(--color-brand-text)] transition-all hover:border-emerald-300/30 hover:bg-emerald-400/14"
                        >
                            <Mail size={16} className="text-emerald-300" />
                            caselabs91@gmail.com
                        </a>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}